<div class="flex flex-col p-6 gap-6">
  <!-- Enhanced Breadcrumbs -->
  <div class="breadcrumbs">
    <ep-breadcrumb [routerLink]="['/']"><span>Realm</span></ep-breadcrumb>
    <ep-breadcrumb
      show-separator="true"
      [routerLink]="['/groups']"
      [queryParams]="{ page: 1 }"
      [active]="breadcrumbs.length === 0"
    >
      <span>Groups</span></ep-breadcrumb
    >
    @for (breadcrumb of breadcrumbs; track breadcrumb.id; let i = $index) {
      <ep-breadcrumb
        show-separator="true"
        [routerLink]="['/groups']"
        [queryParams]="{ path: breadcrumb.path, page: 1 }"
        [active]="i === breadcrumbs.length - 1"
      >
        <span>{{ breadcrumb.name }}</span>
      </ep-breadcrumb>
    }
  </div>

  <!-- Header with current location -->
  <div class="flex items-center justify-between">
    <div>
      <h1>{{ getCurrentLocationText() }}</h1>
      @if (breadcrumbs.length > 0) {
        <p class="body-sm text-gray-600 mt-1">Level {{ breadcrumbs.length }} deep</p>
      }
    </div>
    @if (breadcrumbs.length > 0) {
      <ep-button color="secondary" [routerLink]="['/groups']" [queryParams]="{ page: 1 }">
        Back to All Groups
      </ep-button>
    }
  </div>

  @if (errorMessage) {
    <ep-alert type="toast" variant="alt" color="danger" [center]="true" [bottom]="true">
      <span slot="header">{{ errorMessage }}</span>
    </ep-alert>
  }

  <!-- Search and Controls -->
  <div class="flex justify-between items-center gap-4">
    <div class="w-full max-w-md">
      <ep-textfield
        variant="search"
        [formControl]="searchControl"
        [placeholder]="currentParentId ? 'Search in current group...' : 'Search groups...'"
        class="flex-grow"
        ngDefaultControl
      >
        <ep-button
          slot="suffix"
          color="default"
          variant="quiet"
          size="tiny"
          (click)="clearSearch()"
        >
          <svg class="icon icon--small" viewBox="0 -960 960 960" fill="currentColor">
            <path
              d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z"
            />
          </svg>
        </ep-button>
      </ep-textfield>
    </div>
    <ep-button (click)="refreshGroups()">
      <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
        <path
          d="M480-160q-134 0-227-93t-93-227q0-134 93-227t227-93q69 0 132 28.5T720-690v-110h80v280H520v-80h168q-32-56-87.5-88T480-720q-100 0-170 70t-70 170q0 100 70 170t170 70q77 0 139-44t87-116h84q-28 106-114 173t-196 67Z"
        />
      </svg>
      <span>Refresh</span>
    </ep-button>
  </div>

  @if (!isLoading && currentGroups.length === 0) {
    <div class="text-center py-8 text-gray-500">No groups found</div>
  }

  <!-- Display groups as table instead of ep-items -->
  <!-- Click on row to open group edit in sidebar, use the external link button to open in separate page -->
  @if (!isLoading && currentGroups.length > 0) {
    <div class="overflow-x-auto">
      <table class="w-full table-fixed">
        <colgroup>
          <col class="w-full min-w-[200px]" />
          <col class="w-full min-w-[200px]" />
          <col class="w-1/6 min-w-[140px]" />
          <col class="w-1/6 min-w-[120px]" />
        </colgroup>
        <thead>
          <tr>
            <th class="text-left"><label>Name</label></th>
            <th class="text-left"><label>Path</label></th>
            <th class="text-left"><label>Subgroups</label></th>
            <th class="text-left"><label>Actions</label></th>
          </tr>
        </thead>
        <tbody>
          @for (group of currentGroups; track group.id) {
            <!-- (click)="openGroupEdit(group, $event)" -->
            <tr
              (click)="navigateToGroup(group); $event.stopPropagation()"
              class="cursor-pointer border-t border-stroke-subtle hover:bg-gray-50"
            >
              <td class="">
                <div class="flex items-center gap-3 min-w-0">
                  <!-- Group icon -->
                  <div
                    class="rounded icon icon--large bg-bg-primary-default text-fg-primary-bolder flex items-center justify-center flex-shrink-0"
                  >
                    <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                      <path
                        d="M40-160v-112q0-34 17.5-62.5T104-378q62-31 126-46.5T360-440q66 0 130 15.5T616-378q29 15 46.5 43.5T680-272v112H40Zm720 0v-120q0-44-24.5-84.5T666-434q51 6 96 20.5t84 35.5q36 20 55 44.5t19 53.5v120H760ZM360-480q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 66-47 113t-113 47Zm400-160q0 66-47 113t-113 47q-11 0-28-2.5t-28-5.5q27-32 41.5-71t14.5-81q0-42-14.5-81T544-792q14-5 28-6.5t28-1.5q66 0 113 47t47 113ZM120-240h480v-32q0-11-5.5-20T580-306q-54-27-109-40.5T360-360q-56 0-111 13.5T140-306q-9 5-14.5 14t-5.5 20v32Zm240-320q33 0 56.5-23.5T440-640q0-33-23.5-56.5T360-720q-33 0-56.5 23.5T280-640q0 33 23.5 56.5T360-560Zm0 320Zm0-400Z"
                      />
                    </svg>
                  </div>
                  <span class="body-sm truncate">{{ group.name || 'Unnamed Group' }}</span>
                </div>
              </td>
              <td class="">
                <span class="body-sm truncate block">
                  {{ group.path || '-' }}
                </span>
              </td>
              <td class="">
                <div class="flex items-center gap-2">
                  @if (group.subGroupCount && group.subGroupCount > 0) {
                    <ep-chip>
                      <span class="whitespace-nowrap"
                        >{{ group.subGroupCount }} subgroups</span
                      ></ep-chip
                    >
                  }
                </div>
              </td>
              <td class="">
                <div class="flex items-center gap-1">
                  <ep-button
                    size="small"
                    color="primary"
                    type="quiet"
                    (click)="openGroupEdit(group, $event); $event.stopPropagation()"
                    title="Edit"
                  >
                    <svg class="icon icon--small" viewBox="0 -960 960 960" fill="currentColor">
                      <path
                        d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"
                      />
                    </svg>
                  </ep-button>
                  <!-- Action button for direct navigation -->
                  <ep-button
                    size="small"
                    color="default"
                    type="quiet"
                    [routerLink]="['/group', group.id]"
                    (click)="$event.stopPropagation()"
                    title="Open in separate page"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      ></path>
                    </svg>
                  </ep-button>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
    <ep-pagination
      class="self-center"
      [currentPage]="currentPage"
      [pageSize]="pageSize"
      [totalItems]="totalGroups"
      (change)="goToPage($event)"
    >
    </ep-pagination>
  }
</div>
